import { useEffect, useMemo, useState } from "react";
import { Alert, Button, Card, Col, Container, Form, Modal, Row, Spinner, Table } from "react-bootstrap";
import { ExternalLink, Eye, Inbox, Link2, Mail, Phone, RefreshCw, Search, Unlink } from "lucide-react";
import api from "Common/api";
import "./property-leads.css";

type PropertyLead = {
  pl_id?: number;
  id?: number;
  pl_name?: string;
  pl_prop_id?: string;
  pl_phone?: string;
  pl_email?: string;
  pl_message?: string;
  pl_created_at?: string;
  rep_id?: number;
  rep_title?: string;
  rep_street_address?: string;
  rep_state?: string;
  rep_zip?: string;
  rep_price?: number | string;
  rep_user_id?: string | number;
};

function formatDate(value?: string) {
  if (!value) return { date: "Not available", time: "" };
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return { date: value, time: "" };
  return {
    date: date.toLocaleDateString(undefined, { day: "2-digit", month: "short", year: "numeric" }),
    time: date.toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" }),
  };
}

function leadId(lead: PropertyLead) {
  return lead.pl_id || lead.id || lead.pl_prop_id || `${lead.pl_email}-${lead.pl_created_at}`;
}

export default function PropertyLeadsAdmin() {
  document.title = "Property Leads";

  const [leads, setLeads] = useState<PropertyLead[]>([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [error, setError] = useState("");
  const [selectedLead, setSelectedLead] = useState<PropertyLead | null>(null);

  const filteredLeads = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return leads;

    return leads.filter((lead) =>
      [
        lead.pl_name,
        lead.pl_email,
        lead.pl_phone,
        lead.pl_prop_id,
        lead.pl_message,
        lead.rep_title,
        lead.rep_street_address,
        lead.rep_state,
        lead.rep_zip,
      ]
        .filter(Boolean)
        .some((value) => String(value).toLowerCase().includes(q))
    );
  }, [leads, search]);

  const pageSize = 10;
  const totalPages = Math.max(1, Math.ceil(filteredLeads.length / pageSize));
  const visibleLeads = filteredLeads.slice((currentPage - 1) * pageSize, currentPage * pageSize);
  const linkedLeadCount = leads.filter((lead) => lead.pl_prop_id).length;
  const rangeStart = filteredLeads.length ? (currentPage - 1) * pageSize + 1 : 0;
  const rangeEnd = Math.min(currentPage * pageSize, filteredLeads.length);

  const loadLeads = async () => {
    setLoading(true);
    setError("");

    try {
      const response = await api.get("/real-estate/property-leads/admin?limit=500");
      setLeads((response as any)?.result || []);
    } catch (err: any) {
      setError(err?.response?.data?.error || err?.response?.data?.message || err?.message || "Unable to load property leads");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadLeads();
  }, []);

  const openPublicProperty = (lead: PropertyLead) => {
    if (!lead.pl_prop_id) return;
    window.open(`https://www.relibuild.com/property-detail/${lead.pl_prop_id}`, "_blank", "noopener,noreferrer");
  };

  const copyPropertyId = async (lead: PropertyLead) => {
    if (!lead.pl_prop_id) return;
    await navigator.clipboard.writeText(String(lead.pl_prop_id));
  };

  return (
    <div className="page-content property-leads-page">
      <Container fluid className="property-leads-shell">
        {error ? <Alert variant="danger">{error}</Alert> : null}

        <Row className="g-3 property-lead-stats">
          <Col md={4}>
            <Card className="h-100 property-lead-stat-card">
              <Card.Body>
                <div className="property-lead-stat-icon"><Inbox size={20} /></div>
                <div>
                  <div className="property-lead-stat-label">Total leads</div>
                  <div className="property-lead-stat-value">{leads.length.toLocaleString()}</div>
                </div>
              </Card.Body>
            </Card>
          </Col>
          <Col md={4}>
            <Card className="h-100 property-lead-stat-card">
              <Card.Body>
                <div className="property-lead-stat-icon"><Link2 size={20} /></div>
                <div>
                  <div className="property-lead-stat-label">Linked to property</div>
                  <div className="property-lead-stat-value">{linkedLeadCount.toLocaleString()}</div>
                </div>
              </Card.Body>
            </Card>
          </Col>
          <Col md={4}>
            <Card className="h-100 property-lead-stat-card property-lead-stat-warning">
              <Card.Body>
                <div className="property-lead-stat-icon"><Unlink size={20} /></div>
                <div>
                  <div className="property-lead-stat-label">Not linked</div>
                  <div className="property-lead-stat-value">{(leads.length - linkedLeadCount).toLocaleString()}</div>
                </div>
              </Card.Body>
            </Card>
          </Col>
        </Row>

        <Card className="property-leads-card">
          <Card.Header>
            <div className="property-leads-toolbar">
              <div>
                <h5 className="mb-1">Property Inquiry Leads</h5>
                <div className="property-leads-subtitle">Review inquiries and contact interested customers.</div>
              </div>
              <div className="property-leads-tools">
                <div className="property-leads-search">
                  <Search size={16} aria-hidden="true" />
                  <Form.Control
                    value={search}
                    onChange={(event) => {
                      setSearch(event.target.value);
                      setCurrentPage(1);
                    }}
                    placeholder="Search leads"
                    aria-label="Search property leads"
                  />
                </div>
                <Button className="property-leads-refresh" variant="outline-primary" onClick={loadLeads} disabled={loading}>
                  {loading ? <Spinner size="sm" /> : <RefreshCw size={16} />}
                  Refresh
                </Button>
              </div>
            </div>
          </Card.Header>
          <Card.Body className="p-0">
            <div className="table-responsive">
              <Table className="table-hover align-middle mb-0 property-leads-table">
                <colgroup>
                  <col className="property-leads-col-index" />
                  <col className="property-leads-col-customer" />
                  <col className="property-leads-col-property" />
                  <col className="property-leads-col-message" />
                  <col className="property-leads-col-created" />
                  <col className="property-leads-col-actions" />
                </colgroup>
                <thead className="table-light">
                  <tr>
                    <th>#</th>
                    <th>Customer</th>
                    <th>Property</th>
                    <th>Message</th>
                    <th>Created</th>
                    <th className="text-end">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <tr>
                      <td colSpan={6} className="text-center py-5">
                        <Spinner size="sm" className="me-2" />
                        Loading property leads...
                      </td>
                    </tr>
                  ) : filteredLeads.length ? (
                    visibleLeads.map((lead, index) => {
                      const createdAt = formatDate(lead.pl_created_at);
                      return (
                      <tr key={leadId(lead)}>
                        <td className="property-lead-index">{(currentPage - 1) * pageSize + index + 1}</td>
                        <td>
                          <div className="property-lead-primary">{lead.pl_name || "Not provided"}</div>
                          <div className="property-lead-secondary">{lead.pl_email || "No email"}</div>
                          <div className="property-lead-secondary">{lead.pl_phone || "No phone"}</div>
                        </td>
                        <td>
                          <div className="property-lead-primary">{lead.rep_title || "MLS / cached property"}</div>
                          {lead.pl_prop_id ? (
                            <div className="property-lead-secondary">ID: {lead.pl_prop_id}</div>
                          ) : (
                            <span className="property-lead-unlinked">Not linked</span>
                          )}
                          {[lead.rep_street_address, lead.rep_state, lead.rep_zip].some(Boolean) ? (
                            <div className="property-lead-secondary property-lead-address">
                              {[lead.rep_street_address, lead.rep_state, lead.rep_zip].filter(Boolean).join(", ")}
                            </div>
                          ) : null}
                        </td>
                        <td>
                          <div className="property-lead-message" title={lead.pl_message || "No message"}>{lead.pl_message || "No message"}</div>
                        </td>
                        <td>
                          <div className="property-lead-date">{createdAt.date}</div>
                          {createdAt.time ? <div className="property-lead-time">{createdAt.time}</div> : null}
                        </td>
                        <td>
                          <div className="property-lead-actions">
                            <Button size="sm" variant="primary" onClick={() => setSelectedLead(lead)}>
                              <Eye size={14} /> View
                            </Button>
                            <Button size="sm" variant="outline-secondary" disabled={!lead.pl_email} href={lead.pl_email ? `mailto:${lead.pl_email}` : undefined} aria-label={`Email ${lead.pl_name || "lead"}`}>
                              <Mail size={14} />
                            </Button>
                            <Button size="sm" variant="outline-secondary" disabled={!lead.pl_phone} href={lead.pl_phone ? `tel:${lead.pl_phone}` : undefined} aria-label={`Call ${lead.pl_name || "lead"}`}>
                              <Phone size={14} />
                            </Button>
                            {lead.pl_prop_id ? (
                              <Button size="sm" variant="outline-secondary" onClick={() => openPublicProperty(lead)} aria-label="Open linked property">
                                <ExternalLink size={14} />
                              </Button>
                            ) : null}
                          </div>
                        </td>
                      </tr>
                    );})
                  ) : (
                    <tr>
                      <td colSpan={6} className="text-center py-5 text-muted">
                        No property leads found.
                      </td>
                    </tr>
                  )}
                </tbody>
              </Table>
            </div>
          </Card.Body>
          {!loading && filteredLeads.length > 0 ? (
            <Card.Footer className="property-leads-pagination">
              <span>Showing {rangeStart}–{rangeEnd} of {filteredLeads.length.toLocaleString()}</span>
              <div className="d-flex align-items-center gap-2">
                <Button size="sm" variant="outline-secondary" disabled={currentPage === 1} onClick={() => setCurrentPage((page) => Math.max(1, page - 1))}>Previous</Button>
                <span className="property-leads-page-count">Page {currentPage} of {totalPages}</span>
                <Button size="sm" variant="outline-secondary" disabled={currentPage === totalPages} onClick={() => setCurrentPage((page) => Math.min(totalPages, page + 1))}>Next</Button>
              </div>
            </Card.Footer>
          ) : null}
        </Card>
      </Container>

      <Modal show={!!selectedLead} onHide={() => setSelectedLead(null)} centered size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Property Lead Details</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedLead ? (
            <Row className="g-3">
              <Col md={6}>
                <div className="text-muted small">Name</div>
                <div className="fw-semibold">{selectedLead.pl_name || "-"}</div>
              </Col>
              <Col md={6}>
                <div className="text-muted small">Property ID</div>
                <div className="fw-semibold">{selectedLead.pl_prop_id || "-"}</div>
              </Col>
              <Col md={6}>
                <div className="text-muted small">Email</div>
                <div className="fw-semibold">{selectedLead.pl_email || "-"}</div>
              </Col>
              <Col md={6}>
                <div className="text-muted small">Phone</div>
                <div className="fw-semibold">{selectedLead.pl_phone || "-"}</div>
              </Col>
              <Col md={12}>
                <div className="text-muted small">Message</div>
                <div className="property-lead-modal-message">{selectedLead.pl_message || "No message"}</div>
              </Col>
            </Row>
          ) : null}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="light" onClick={() => setSelectedLead(null)}>
            Close
          </Button>
          <Button variant="outline-secondary" disabled={!selectedLead?.pl_prop_id} onClick={() => selectedLead && copyPropertyId(selectedLead)}>
            Copy Property ID
          </Button>
          <Button variant="primary" disabled={!selectedLead?.pl_prop_id} onClick={() => selectedLead && openPublicProperty(selectedLead)}>
            Open Property
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
}
